import {
  Alert,
  Button,
  Group,
  NumberInput,
  Paper,
  Radio,
  Select,
  Stack,
  Stepper,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEinrichtungList } from '@/api/useEinrichtung';
import { useTraegerList } from '@/api/useTraeger';
import { useClonePlan, useCreatePlan, usePlaeneList } from '@/api/usePlaene';
import { useAppStore } from '@/store/appStore';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import type { PlanCreateInput } from '@/api/types';

type Source = 'empty' | 'clone' | 'from_ist';

export function PlanWizardPage() {
  const { t } = useTranslation(['plaene', 'common']);
  const navigate = useNavigate();
  const traegerId = useAppStore((s) => s.activeTraegerId);
  const einrichtungId = useAppStore((s) => s.activeEinrichtungId);
  const jahr = useAppStore((s) => s.activeFoerderjahr);

  const traegerQuery = useTraegerList();
  const einrichtungenQuery = useEinrichtungList(traegerId);
  const previousPlaeneQuery = usePlaeneList({
    einrichtung_id: einrichtungId ?? undefined,
  });

  const [active, setActive] = useState(0);
  const [source, setSource] = useState<Source>('empty');
  const [name, setName] = useState('');
  const [selectedSourcePlan, setSelectedSourcePlan] = useState<string | null>(null);
  const [gruppenAnzahl, setGruppenAnzahl] = useState<number>(2);
  const [initialQuote, setInitialQuote] = useState<number>(95);
  const [personalVorlage, setPersonalVorlage] = useState<string>('standard');

  const createMutation = useCreatePlan();
  const cloneMutation = useClonePlan();

  const traeger = traegerQuery.data?.find((tr) => tr.id === traegerId) ?? null;
  const finanzServiceIntern = traeger?.finanzservice_intern === true;

  if (traegerQuery.isLoading || einrichtungenQuery.isLoading) return <LoadingState />;
  if (traegerQuery.isError) return <ErrorState error={traegerQuery.error} />;
  if (einrichtungenQuery.isError) return <ErrorState error={einrichtungenQuery.error} />;

  // 4 Steps (0..3): Quelle, Gruppen, Belegung, Personal-Vorlage.
  // Pauschalen werden im Plan-Editor → Tab "Finanzierung" gesetzt.
  const LAST_STEP = 3;
  const next = () => setActive((s) => Math.min(s + 1, LAST_STEP));
  const back = () => setActive((s) => Math.max(s - 1, 0));

  const handleFinish = async () => {
    if (!einrichtungId) return;
    if (source === 'clone' && selectedSourcePlan) {
      cloneMutation.mutate(
        {
          source_plan_id: selectedSourcePlan,
          target_foerderjahr: jahr,
          new_name: name,
          adjust_tarif: true,
          copy_belegung: true,
          copy_personal: true,
          copy_sach: true,
          copy_finanz: true,
        },
        { onSuccess: (plan) => navigate(`/plaene/${plan.id}`) },
      );
      return;
    }
    const payload: PlanCreateInput = {
      einrichtung_id: einrichtungId,
      foerderjahr: jahr,
      name,
      source,
      template_options: {
        gruppen_anzahl: gruppenAnzahl,
        initial_belegungsquote: initialQuote,
        personal_vorlage: personalVorlage,
      },
    };
    if (source === 'clone' && selectedSourcePlan) {
      payload.source_plan_id = selectedSourcePlan;
    }
    createMutation.mutate(payload, {
      onSuccess: (plan) => navigate(`/plaene/${plan.id}`),
    });
  };

  return (
    <Stack>
      <Title order={2}>{t('plaene:wizard.title')}</Title>

      <Paper withBorder p="md">
        <Stepper active={active} onStepClick={setActive}>
          <Stepper.Step label={t('plaene:wizard.step1.title')}>
            <Stack mt="md">
              <Radio.Group value={source} onChange={(v) => setSource(v as Source)}>
                <Stack>
                  <Radio value="empty" label={t('plaene:wizard.step1.empty')} />
                  <Radio value="clone" label={t('plaene:wizard.step1.clone')} />
                  <Radio
                    value="from_ist"
                    label={t('plaene:wizard.step1.fromIst')}
                    disabled={!finanzServiceIntern}
                    description={
                      !finanzServiceIntern ? t('plaene:wizard.step1.fromIstHint') : undefined
                    }
                  />
                </Stack>
              </Radio.Group>
              {source === 'clone' ? (
                <Select
                  label={t('plaene:wizard.step1.clone')}
                  placeholder={t('common:state.empty')}
                  data={(previousPlaeneQuery.data ?? []).map((p) => ({
                    value: p.id,
                    label: `${p.name} (${p.foerderjahr})`,
                  }))}
                  value={selectedSourcePlan}
                  onChange={setSelectedSourcePlan}
                  searchable
                />
              ) : null}
              <TextInput
                label={t('plaene:col.name')}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                required
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label={t('plaene:wizard.step2.title')}>
            <Stack mt="md">
              <Text c="dimmed">{t('plaene:wizard.step2.subtitle')}</Text>
              <NumberInput
                label={t('plaene:wizard.step2.title')}
                value={gruppenAnzahl}
                onChange={(v) => setGruppenAnzahl(typeof v === 'number' ? v : Number(v) || 1)}
                min={1}
                max={20}
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label={t('plaene:wizard.step3.title')}>
            <Stack mt="md">
              <Text c="dimmed">{t('plaene:wizard.step3.subtitle')}</Text>
              <NumberInput
                label="%"
                value={initialQuote}
                onChange={(v) => setInitialQuote(typeof v === 'number' ? v : Number(v) || 0)}
                min={0}
                max={120}
                suffix=" %"
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label={t('plaene:wizard.step4.title')}>
            <Stack mt="md">
              <Text c="dimmed">{t('plaene:wizard.step4.subtitle')}</Text>
              <Select
                label={t('plaene:wizard.step4.title')}
                data={[
                  { value: 'standard', label: 'Standard' },
                  { value: 'minimal', label: 'Minimal' },
                  { value: 'erweitert', label: 'Erweitert' },
                ]}
                value={personalVorlage}
                onChange={(v) => v && setPersonalVorlage(v)}
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Completed>
            <Alert variant="light" color="green" mt="md">
              {t('plaene:info.createSuccess')}
            </Alert>
          </Stepper.Completed>
        </Stepper>

        <Group justify="space-between" mt="lg">
          <Button variant="default" onClick={() => navigate('/plaene')}>
            {t('plaene:wizard.cancel')}
          </Button>
          <Group>
            {active > 0 ? (
              <Button variant="default" onClick={back}>
                {t('plaene:wizard.back')}
              </Button>
            ) : null}
            {active < LAST_STEP ? (
              <Button onClick={next} disabled={active === 0 && !name}>
                {t('plaene:wizard.next')}
              </Button>
            ) : (
              <Button
                onClick={() => void handleFinish()}
                loading={createMutation.isPending || cloneMutation.isPending}
                disabled={!einrichtungId || !name}
              >
                {t('plaene:wizard.finish')}
              </Button>
            )}
          </Group>
        </Group>
      </Paper>
    </Stack>
  );
}
